import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import StorageIcon from "@mui/icons-material/Storage";

import CustomSnackbar from "./CustomSnackbar";
import { isConnectionVerified } from "../utils/api";
import { BRAND_COLOR_TINT, CARD_BORDER, INFO_PANEL_BG } from "../utils/colors";
import {
  FS_BODY,
  FS_CAPTION,
  FS_TITLE,
  FW_SEMIBOLD,
} from "../utils/typography";
const ZOHO = window.ZOHO;

// Every form field, explicitly. Inputs must go back to "" — never undefined.
// A text input whose value changes from a string to undefined does NOT clear:
// React stops controlling it and the DOM keeps showing the old text. That is
// exactly how a disconnect once reset the port but left username/password/
// host/database visibly populated.
const EMPTY_FORM = {
  Connection_Url: "",
  username: "",
  password: "",
  hostname: "",
  port: "5432",
  database: "",
  sslOptions: "",
};

const FieldRow = ({ label, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      width: "100%",
    }}
  >
    <Typography sx={{ width: 140, flexShrink: 0, fontSize: FS_BODY }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const DBConnection = ({
  setTabNumber,
  setDisabledTabs,
  orgId,
  apiKey,
  handleConnectionUrl,
}) => {
  const [showBackdrop, setShowBackDrop] = useState(false);
  const [validConnectionUrl, setValidConnectionUrl] = useState(false);
  // A verified connection that is actually SAVED to the org variable — not
  // merely a URL the user has tested. This is what locks the form and swaps
  // Save for Disconnect.
  const [isConnected, setIsConnected] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  // Modules still configured against this connection. Disconnecting with any
  // in place would orphan their sync jobs, so we block it (spec §6).
  const [moduleCount, setModuleCount] = useState(0);
  const [loader, setLoader] = useState({
    test_conn: false,
    save_conn: false,
  });

  // While connected the form is read-only; disconnecting re-opens it.
  const fieldsLocked = isConnected;

  //snackbar state and functions
  const [openSnack, setOpenSnack] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState(""); // success or error

  const handleClickSnack = () => {
    setOpenSnack(true);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: { ...EMPTY_FORM },
  });

  useEffect(() => {
    (async () => {
      const orgData = {
        apiKeys: [
          process.env.REACT_APP_EXTENSION_IDENTIFIER + "__DB_Connection_URL",
        ],
      };

      const orgVariables = await ZOHO.CRM.API.getOrgVariable(orgData);

      let connectionUrl = orgVariables?.Success?.Content;

      if (!connectionUrl || connectionUrl === "null") {
        return;
      }

      setShowBackDrop(true);

      try {
        const testConnUrl = await axios.request({
          url: `${process.env.REACT_APP_DBURL}/verify`,
          method: "POST",
          headers: {
            orgid: orgId, // Org ID
            apikey: apiKey, // API KEy
            connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
          },
          data: {
            Connection_Url: connectionUrl,
          },
        });

        // `{data:true}` vs `{data:false,error}` — both truthy as objects, so
        // the result has to be read, not just the response body.
        if (isConnectionVerified(testConnUrl?.data)) {
          setValidConnectionUrl(true);
          setIsConnected(true);
          handleConnectionUrl(connectionUrl);
        } else {
          // A saved URL that fails verification stays EDITABLE so the client
          // can fix it — do not lock the form on a broken connection (§6).
          setValidConnectionUrl(false);
          setIsConnected(false);
          handleConnectionUrl(null);
          return;
        }
        setValue("Connection_Url", connectionUrl);

        const regex = /\/\/(.+):(.+)@(.*?)(?::(\d+))?\/(\w+)\??(ssl=.+)?/;
        const connectionString = connectionUrl;

        const match = connectionString.match(regex);

        if (!match) {
          setValue("username", "");
          setValue("password", "");
          setValue("hostname", "");
          setValue("database", "");
          setValue("sslOptions", "");
          return;
        }

        const [, username, password, hostname, port, database, ssl] = match;
        setValue("username", username);
        setValue("password", password);
        setValue("hostname", hostname);
        setValue("database", database);
        setValue("sslOptions", ssl?.replace("ssl=", "") || "");
        setShowBackDrop(false);
      } catch (err) {
        setMessage(JSON.stringify(err));
        setSeverity("error");
        handleClickSnack();
        setShowBackDrop(false);
      } finally {
        setShowBackDrop(false);
      }
    })();
  }, []);

  // rebuild the connection url from the manual credential fields
  const rebuildConnectionUrl = (overrides = {}) => {
    const username = overrides.username ?? getValues("username");
    const password = overrides.password ?? getValues("password");
    const hostname = overrides.hostname ?? getValues("hostname");
    const port = overrides.port ?? getValues("port");
    const database = overrides.database ?? getValues("database");
    const sslOptions = overrides.sslOptions ?? getValues("sslOptions");
    const connection_url = `postgresql://${username}:${password}@${hostname}${
      port && `:${port}`
    }/${database}${sslOptions && `?ssl=${sslOptions}`}`;

    setValue("Connection_Url", connection_url);
  };

  const onsubmit = async (data) => {
    const connectionValue = getValues("Connection_Url");

    if (connectionValue && validConnectionUrl === false) {
      setMessage("Please test your Connection Url!");
      setSeverity("error");
      handleClickSnack();
      return;
    }

    try {
      setLoader((prev) => ({ ...prev, save_conn: true }));
      //we will this endpoint to verify connection url
      const testConnUrl = await axios.request({
        url: `${process.env.REACT_APP_DBURL}/verify`,
        method: "POST",
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
        data: {
          Connection_Url: data.Connection_Url,
        },
      });

      // Save only on a genuine pass. The backend rejects with HTTP 200 and
      // `{data:false}`, which does not throw — without this gate a bad URL
      // was written to the org variable and the tabs unlocked behind it.
      if (!isConnectionVerified(testConnUrl?.data)) {
        setValidConnectionUrl(false);
        setIsConnected(false);
        handleConnectionUrl(null);
        setError("Connection_Url", {
          type: "custom",
          message: testConnUrl?.data?.error || "Connection Url is wrong!",
        });
        setMessage(testConnUrl?.data?.error || "Connection Url is wrong!");
        setSeverity("error");
        handleClickSnack();
        return;
      }

      setValidConnectionUrl(true);
      // Saved AND verified — the form locks from here until Disconnect.
      setIsConnected(true);

      setMessage("Connection Url tested successfully!");
      setSeverity("success");

      handleClickSnack();

      handleConnectionUrl(data.Connection_Url);

      // db url to org variable
      const dbConnData = {
        apiname:
          process.env.REACT_APP_EXTENSION_IDENTIFIER + "__DB_Connection_URL",
        value: data.Connection_Url,
      };

      const resp = await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", dbConnData);

      clearErrors("Connection_Url");

      setDisabledTabs((prev) => {
        return {
          ...prev,
          settingsTab: false,
        };
      });

      setTimeout(() => {
        handleCloseSnack();
        setTabNumber(2);
      }, 700);
    } catch (err) {
      setValidConnectionUrl(false);
      setError("Connection_Url", {
        type: "custom",
        message: "Something Went Wrong",
      });
      setMessage("Connection Url is wrong!");
      setSeverity("error");
      handleClickSnack();
      handleConnectionUrl(null);
    } finally {
      setLoader((prev) => ({ ...prev, save_conn: false }));
      setOpenSnack(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const connectionValue = getValues("Connection_Url");
      setLoader((prev) => ({ ...prev, test_conn: true }));
      //we will this endpoint to verify connection url
      const testConnUrl = await axios.request({
        url: `${process.env.REACT_APP_DBURL}/verify`,
        method: "POST",
        headers: {
          orgid: orgId, // Org ID
          apikey: apiKey, // API KEy
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER, // Conn Name
        },
        data: {
          Connection_Url: connectionValue,
        },
      });

      if (isConnectionVerified(testConnUrl?.data)) {
        clearErrors("Connection_Url");
        setValidConnectionUrl(true);
        setMessage("Connection Url tested successfully!");
        setSeverity("success");
        handleClickSnack();
      } else {
        // A rejected connection is a 200 with `{data:false}` — it never
        // throws, so without this branch the failure passed silently.
        setValidConnectionUrl(false);
        setError("Connection_Url", {
          type: "custom",
          message: testConnUrl?.data?.error || "Connection Url is wrong!",
        });
        setMessage(testConnUrl?.data?.error || "Connection Url is wrong!");
        setSeverity("error");
        handleClickSnack();
      }
    } catch (err) {
      setValidConnectionUrl(false);
      setError("Connection_Url", {
        type: "custom",
        message: "Connection Url is wrong!",
      });
      setMessage("Connection Url is wrong!");
      setSeverity("error");
      handleConnectionUrl(null);
      handleClickSnack();
    } finally {
      setLoader((prev) => ({ ...prev, test_conn: false }));
    }
  };

  // Opening the dialog first checks whether modules are still configured —
  // disconnecting underneath them would orphan their sync jobs, so that case
  // becomes "delete your modules first" rather than a confirm prompt (§6).
  const handleOpenDisconnect = async () => {
    setDisconnectLoading(true);
    try {
      const resp = await axios.request({
        url: `${process.env.REACT_APP_ADMIN_SERVER_URL}/db/planetscale/clients/settings`,
        method: "GET",
        headers: {
          orgid: orgId,
          apikey: apiKey,
          connname: process.env.REACT_APP_EXTENSION_IDENTIFIER,
        },
      });
      const modules = (resp?.data?.data || []).filter(
        (row) => row?.module_name !== "ConnectionSchema"
      );
      setModuleCount(modules.length);
    } catch (err) {
      // Can't prove it's safe, so assume it isn't and let the dialog say so
      // rather than silently allowing a destructive disconnect.
      setModuleCount(-1);
    } finally {
      setDisconnectLoading(false);
      setDisconnectOpen(true);
    }
  };

  const handleConfirmDisconnect = async () => {
    setDisconnectLoading(true);
    try {
      // "null" is the literal string Zoho stores for "unset" — not an empty
      // value and not JSON null.
      await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", {
        apiname:
          process.env.REACT_APP_EXTENSION_IDENTIFIER + "__DB_Connection_URL",
        value: "null",
      });

      // Reset EVERY field to "" (reset also clears validation errors, so the
      // "could not connect" message under the URL goes away with the values).
      reset({ ...EMPTY_FORM });
      setValidConnectionUrl(false);
      setIsConnected(false);
      handleConnectionUrl(null);

      // Everything downstream of a database is unreachable again.
      setDisabledTabs((prev) => ({
        ...prev,
        settingsTab: true,
        backupData: true,
        historical: true,
      }));

      setDisconnectOpen(false);
      setMessage("Database disconnected.");
      setSeverity("success");
      handleClickSnack();
    } catch (err) {
      setMessage("Could not disconnect. Please try again.");
      setSeverity("error");
      handleClickSnack();
    } finally {
      setDisconnectLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Backdrop
        sx={{ color: "primary.main", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Card sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              bgcolor: BRAND_COLOR_TINT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StorageIcon color="primary" sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: FW_SEMIBOLD, fontSize: FS_TITLE }}>
              DB Connection
            </Typography>
            <Typography sx={{ fontSize: FS_BODY, color: "text.secondary" }}>
              Connect the PostgreSQL database your CRM records will sync to.
            </Typography>
          </Box>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onsubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* connection url — FIRST */}
          <FieldRow label="Connection Url">
            <Controller
              control={control}
              name="Connection_Url"
              render={({ field, fieldState }) => (
                <TextField
                  disabled={fieldsLocked}
                  size="small"
                  id="connection_url"
                  variant="outlined"
                  value={field.value}
                  fullWidth
                  placeholder="postgresql://user:password@host:5432/database"
                  error={errors["Connection_Url"]}
                  helperText={errors?.["Connection_Url"]?.message}
                  onChange={(e) => {
                    const connectionUrl = e.target.value;
                    field.onChange(e.target.value);
                    setValidConnectionUrl(false);

                    const regex =
                      /\/\/(.+):(.+)@(.*?)(?::(\d+))?\/(\w+)\??(ssl=.+)?/;
                    const connectionString = e.target.value;

                    const match = connectionString.match(regex);

                    if (match) {
                      const [
                        ,
                        username,
                        password,
                        hostname,
                        port,
                        database,
                        ssl,
                      ] = match;

                      setValue("port", port);
                      setValue("username", username);
                      setValue("password", password);
                      setValue("hostname", hostname);
                      setValue("database", database);
                      setValue("sslOptions", ssl?.replace("ssl=", "") || "");
                    } else {
                      setValue("username", "");
                      setValue("password", "");
                      setValue("hostname", "");
                      setValue("database", "");
                      setValue("sslOptions", "");
                    }
                  }}
                />
              )}
            />
          </FieldRow>

          <Divider sx={{ fontSize: FS_CAPTION, color: "text.secondary" }}>
            or enter credentials manually
          </Divider>

          {/* username */}
          <FieldRow label="UserName">
            <Controller
              control={control}
              name="username"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message}
                  size="small"
                  id="username"
                  variant="outlined"
                  fullWidth
                  {...field}
                  disabled={fieldsLocked}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValidConnectionUrl(false);
                    rebuildConnectionUrl({ username: e.target.value });
                  }}
                />
              )}
            />
          </FieldRow>

          {/* password */}
          <FieldRow label="Password">
            <Controller
              control={control}
              name="password"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextField
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message}
                  size="small"
                  id="password"
                  variant="outlined"
                  fullWidth
                  {...field}
                  disabled={fieldsLocked}
                  type="password"
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValidConnectionUrl(false);
                    rebuildConnectionUrl({ password: e.target.value });
                  }}
                />
              )}
            />
          </FieldRow>

          {/* host and port */}
          <FieldRow label="Host">
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Controller
                control={control}
                name="hostname"
                render={({ field, fieldState }) => (
                  <TextField
                    size="small"
                    id="hostname"
                    variant="outlined"
                    error={!!fieldState.error}
                    helperText={fieldState?.error?.message}
                    {...field}
                    disabled={fieldsLocked}
                    sx={{ flex: 1 }}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValidConnectionUrl(false);
                      rebuildConnectionUrl({ hostname: e.target.value });
                    }}
                  />
                )}
              />

              <Typography sx={{ fontSize: FS_BODY }}>Port</Typography>

              <Controller
                control={control}
                name="port"
                render={({ field }) => (
                  <TextField
                    size="small"
                    id="port"
                    variant="outlined"
                    {...field}
                    disabled={fieldsLocked}
                    sx={{ width: 120 }}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValidConnectionUrl(false);
                      rebuildConnectionUrl({ port: e.target.value });
                    }}
                  />
                )}
              />
            </Box>
          </FieldRow>

          {/* database */}
          <FieldRow label="Database">
            <Controller
              control={control}
              name="database"
              rules={{ required: "This is required Field" }}
              render={({ field, fieldState }) => (
                <TextField
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message}
                  size="small"
                  id="database"
                  variant="outlined"
                  fullWidth
                  {...field}
                  disabled={fieldsLocked}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setValidConnectionUrl(false);
                    rebuildConnectionUrl({ database: e.target.value });
                  }}
                />
              )}
            />
          </FieldRow>

          {/* ssl options */}
          <FieldRow label="SSL options">
            <Controller
              control={control}
              name="sslOptions"
              render={({ field }) => (
                <TextField
                  size="small"
                  id="ssl-options"
                  variant="outlined"
                  fullWidth
                  {...field}
                  disabled={fieldsLocked}
                  onChange={(e) => {
                    field.onChange(e.target.value || "");
                    setValidConnectionUrl(false);
                    rebuildConnectionUrl({ sslOptions: e.target.value });
                  }}
                />
              )}
            />
          </FieldRow>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            {/* Connected: Disconnect + Test, both outlined, Save hidden (§6).
                Not connected: Test + Save. */}
            {isConnected && (
              <LoadingButton
                loading={disconnectLoading}
                variant="outlined"
                color="error"
                onClick={handleOpenDisconnect}
                sx={{ minWidth: 150 }}
              >
                Disconnect
              </LoadingButton>
            )}

            <LoadingButton
              loading={loader.test_conn}
              variant="outlined"
              onClick={handleTestConnection}
              sx={{ minWidth: 150 }}
            >
              Test Connection
            </LoadingButton>

            {!isConnected && (
              <LoadingButton
                type="submit"
                loading={loader.save_conn}
                variant="contained"
                sx={{ minWidth: 150 }}
              >
                Save Connection
              </LoadingButton>
            )}
          </Box>
        </Box>

        <CustomSnackbar
          open={openSnack}
          handleClose={handleCloseSnack}
          message={message}
          severity={severity}
        />
      </Card>

      {/* Disconnect re-confirm. If modules are still configured this becomes
          an explanation instead of a confirmation — disconnecting underneath
          them orphans their sync jobs. */}
      <Dialog
        open={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: FS_TITLE, fontWeight: FW_SEMIBOLD }}>
          {moduleCount > 0 ? "Delete your modules first" : "Disconnect database?"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: FS_BODY, color: "text.secondary" }}>
            {moduleCount > 0
              ? `${moduleCount} module${
                  moduleCount === 1 ? " is" : "s are"
                } still syncing to this database. Delete ${
                  moduleCount === 1 ? "it" : "them"
                } from the Settings tab before changing the connection.`
              : moduleCount < 0
              ? "We couldn't check whether any modules are still configured. Please try again in a moment."
              : "This clears the saved connection and disables the Settings, Backup and Historical tabs until you connect a database again. Your data in PostgreSQL is not deleted."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisconnectOpen(false)}>
            {moduleCount === 0 ? "Cancel" : "Close"}
          </Button>
          {moduleCount === 0 && (
            <LoadingButton
              loading={disconnectLoading}
              variant="contained"
              color="error"
              onClick={handleConfirmDisconnect}
            >
              Disconnect
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      {/* helper note */}
      <Box
        sx={{
          p: 2,
          borderRadius: "10px",
          // helper/explainer panel — spec §10 INFO_PANEL_BG
          bgcolor: INFO_PANEL_BG,
          border: CARD_BORDER,
        }}
      >
        <Typography sx={{ fontSize: FS_BODY, color: "text.secondary", mb: 1 }}>
          Provide the host name, port, username, password, and database name of
          your PostgreSQL server — or paste a full connection URL and the
          fields will fill themselves in.
        </Typography>
        <Typography sx={{ fontSize: FS_BODY, color: "text.secondary" }}>
          For enhanced security and to ensure uninterrupted connectivity, it's
          crucial to whitelist our{" "}
          <span style={{ fontWeight: FW_SEMIBOLD }}>IP address %%%%</span> in your
          database firewall settings / allowed hosts (pg_hba.conf) of your
          PostgreSQL server. This is a vital step for a secure and reliable
          connection.
        </Typography>
      </Box>
    </Box>
  );
};

export default DBConnection;
